import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { TrendingUp, Clock, Award, FileText, Phone, AlertTriangle, AlertCircle, CheckCircle, BookOpen, UserX, Timer, FileCheck, Users, User, Calendar, GraduationCap, Building2, UserCheck, Download, PenTool, Receipt, X, Mail, Shield, ChevronDown, ChevronUp, ExternalLink, Eye, CreditCard } from 'lucide-react';
import CompetencesValidees from '../components/CompetencesValidees';
import CompetencesPrerequisConsultation from '../components/CompetencesPrerequisConsultation';
import EPMSPSectionStagiaire from '../components/EPMSPSectionStagiaire';
import ZenButton from '../components/ZenButton';
import DemandeAbsenceJustifiee from '../components/DemandeAbsenceJustifiee';
import GrilleEvaluationReadOnly from '../components/GrilleEvaluationReadOnly';
import NotificationWidget from '../components/NotificationWidget';
import AutonomieActionCard from '../components/AutonomieActionCard';
import CarteEtudiantModal from '../components/CarteEtudiantModal';
import { PhotoIdentite } from '../components/PhotoIdentite';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { openPdfBlob } from '../utils/pdfOpener';
import { DocumentsAccueil } from '../components/livret-accueil/DocumentsAccueil';
import { TuileLivretBase } from '../components/livret-base/TuileLivretBase';

const DashboardStagiaire = ({ user }) => {
  const [stagiaire, setStagiaire] = useState(null);
  const [stats, setStats] = useState(null);
  const [heuresTotal, setHeuresTotal] = useState(0);
  const [autonomiesEnAttente, setAutonomiesEnAttente] = useState(0);
  const [autonomiesValidees, setAutonomiesValidees] = useState([]);
  const [presenceStats, setPresenceStats] = useState({ absences: 0, retards: 0, justifiees: 0 });
  const [moyenneClasse, setMoyenneClasse] = useState(null);
  
  // Mes Devis
  const [mesDevis, setMesDevis] = useState([]);
  const [mesFactures, setMesFactures] = useState([]);
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const signatureRef = useRef(null);
  
  // PIF et Convention (données candidat)
  const [pifData, setPifData] = useState(null);
  const [conventionData, setConventionData] = useState(null);
  
  // Documents candidat pour barre de progression
  const [candidatDocs, setCandidatDocs] = useState([]);
  
  // Dossier validation status from centre
  const [dossierStatus, setDossierStatus] = useState(null);
  
  // Resultats tests selection
  const [testsSelection, setTestsSelection] = useState(null);
  
  // Convention de Mise en Situation Pédagogique (tripartite)
  const [convStage, setConvStage] = useState(null);
  
  // Carte Etudiant
  const [carteOpen, setCarteOpen] = useState(false);
  const [centreProfile, setCentreProfile] = useState(null);
  const [showSignConvStageDialog, setShowSignConvStageDialog] = useState(false);
  const [useProfileSig, setUseProfileSig] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const convStageSignRef = useRef(null);

  // Félicitations diplôme
  const [showDiplomeDialog, setShowDiplomeDialog] = useState(false);

  useEffect(() => {
    loadStagiaireData();
    loadMesDevis();
    loadCandidatDocuments();
    loadConventionStage();
    loadSavedSignature();
    checkDiplome();
    loadCentreProfile();
  }, []);

  const checkDiplome = async () => {
    try {
      const res = await axios.get(`/stagiaires/${user.user_id}/competences-prerequis`);
      if (res.data.diplome_obtenu) {
        const dismissed = sessionStorage.getItem(`diplome_felicitations_${user.user_id}`);
        if (!dismissed) {
          setShowDiplomeDialog(true);
        }
      }
    } catch (e) {
      // silencieux
    }
  };

  const dismissDiplomeDialog = () => {
    setShowDiplomeDialog(false);
    sessionStorage.setItem(`diplome_felicitations_${user.user_id}`, 'true');
  };

  const loadStagiaireData = async () => {
    try {
      const [stagiaireRes, statsRes, heuresRes, autonomiesRes, presencesRes, moyenneClasseRes] = await Promise.all([
        axios.get(`/stagiaires/${user.user_id}`),
        axios.get(`/notes/stats/${user.user_id}`),
        axios.get(`/heures-stage/stagiaire/${user.user_id}`),
        axios.get(`/autonomies-en-attente/stagiaire/${user.user_id}`),
        axios.get(`/presences/stagiaire/${user.user_id}`),
        axios.get('/notes/moyenne-classe').catch(() => ({ data: { moyenne: null } }))
      ]);

      setStagiaire(stagiaireRes.data);
      setStats(statsRes.data);
      setHeuresTotal(heuresRes.data.total);
      setMoyenneClasse(moyenneClasseRes.data?.moyenne);
      
      // Calculer les stats de présence
      const presences = presencesRes.data || [];
      const absences = presences.filter(p => p.statut === 'absent').length;
      const retards = presences.filter(p => p.statut === 'retard').length;
      const justifiees = presences.filter(p => p.statut === 'absent' && p.justifie === true).length;
      setPresenceStats({ absences, retards, justifiees });
      
      const autonomies = autonomiesRes.data || [];
      setAutonomiesEnAttente(autonomies.filter(a => a.en_attente).length);
      setAutonomiesValidees(autonomies.filter(a => a.stagiaire_reponse === true));
    } catch (error) {
    }
  };

  const loadMesDevis = async () => {
    try {
      const response = await axios.get('/stagiaire/mes-devis');
      setMesDevis(response.data.devis || []);
      setMesFactures(response.data.factures || []);
    } catch (error) {
    }
  };

  const handleDownloadDevis = async (devisId) => {
    try {
      const response = await axios.get(`/stagiaire/devis/${devisId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${user?.nom || 'stagiaire'}_${user?.prenom || ''}_devis.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Devis téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const loadCandidatDocuments = async () => {
    try {
      const [pifRes, convRes, docsRes, dossierStatusRes] = await Promise.all([
        axios.get('/candidats/mon-pif').catch(() => ({ data: null })),
        axios.get('/candidats/ma-convention').catch(() => ({ data: {} })),
        axios.get(`/candidats/${user.user_id}/documents`).catch(() => ({ data: { documents: [] } })),
        axios.get('/candidats/mon-dossier-status').catch(() => ({ data: null }))
      ]);
      if (pifRes.data && pifRes.data.statut) setPifData(pifRes.data);
      if (convRes.data && convRes.data.statut) setConventionData(convRes.data);
      const docs = docsRes.data?.documents || docsRes.data || [];
      setCandidatDocs(docs);
      if (dossierStatusRes.data) setDossierStatus(dossierStatusRes.data);
      
      // Charger les résultats tests depuis le dossier candidat
      try {
        const dossierRes = await axios.get(`/stagiaires/${user.user_id}/dossier-candidat`);
        const tests = dossierRes.data?.tests_selection || dossierRes.data?.candidat_profil?.tests_selection;
        if (tests && tests.resultat_global) setTestsSelection(tests);
      } catch (e) {}
    } catch (error) {
    }
  };

  const loadConventionStage = async () => {
    try {
      const res = await axios.get(`/convention-stage/${user.user_id}`);
      if (res.data && res.data.statut) {
        setConvStage(res.data);
      }
    } catch (error) {
    }
  };

  const loadCentreProfile = async () => {
    try {
      const centreRes = await axios.get('/centres-profiles');
      const centreName = stagiaire?.centre_formation;
      if (centreName && centreRes.data?.[centreName]) {
        setCentreProfile(centreRes.data[centreName]);
      }
    } catch (e) {}
  };

  const loadSavedSignature = async () => {
    try {
      const res = await axios.get(`/signatures/${user.user_id}`);
      if (res.data?.signature) {
        setSavedSignature(res.data.signature);
        setUseProfileSig(true);
        setUseProfileSigDevis(true);
        return;
      }
    } catch (e) {}
    // Fallback: charger depuis le profil candidat
    try {
      const res = await axios.get('/candidats/ma-signature');
      if (res.data?.signature) {
        setSavedSignature(res.data.signature);
        setUseProfileSig(true);
        setUseProfileSigDevis(true);
      }
    } catch (e) {}
  };

  const handleSignConvStage = async () => {
    let signature;
    if (useProfileSig && savedSignature) {
      signature = savedSignature;
    } else {
      if (!convStageSignRef.current || convStageSignRef.current.isEmpty()) {
        toast.error('Veuillez dessiner votre signature');
        return;
      }
      signature = convStageSignRef.current.toDataURL();
    }
    try {
      await axios.post(`/convention-stage/${user.user_id}/signer`, { signature });
      toast.success('Convention de stage signée !');
      setShowSignConvStageDialog(false);
      setUseProfileSig(false);
      loadConventionStage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const _convFileName = (suffix) => `${user?.nom || 'stagiaire'}_${user?.prenom || ''}_${suffix}.pdf`;

  const handleViewConvStage = async () => {
    try {
      const res = await axios.get(`/convention-stage/${user.user_id}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, _convFileName('Convention_Mise_Situation_Pedagogique'), 'view');
    } catch {
      toast.error('Impossible de charger le PDF');
    }
  };

  const handleDownloadConvStage = async () => {
    try {
      const res = await axios.get(`/convention-stage/${user.user_id}/pdf`, { responseType: 'blob' });
      openPdfBlob(res.data, _convFileName('Convention_Mise_Situation_Pedagogique'), 'download');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDownloadPIF = async () => {
    try {
      const response = await axios.get('/candidats/mon-pif/pdf', { responseType: 'blob' });
      openPdfBlob(response.data, _convFileName('PIF'), 'download');
    } catch {
      toast.error('Erreur lors du téléchargement du PIF');
    }
  };

  const handleViewPIF = async () => {
    try {
      const response = await axios.get('/candidats/mon-pif/pdf', { responseType: 'blob' });
      openPdfBlob(response.data, _convFileName('PIF'), 'view');
    } catch {
      toast.error('Impossible de charger le PIF');
    }
  };

  const handleDownloadConvention = async () => {
    try {
      const response = await axios.get('/candidats/ma-convention/pdf', { responseType: 'blob' });
      openPdfBlob(response.data, _convFileName('Convention_Formation'), 'download');
    } catch {
      toast.error('Erreur lors du téléchargement de la Convention');
    }
  };

  const handleViewConvention = async () => {
    try {
      const response = await axios.get('/candidats/ma-convention/pdf', { responseType: 'blob' });
      openPdfBlob(response.data, _convFileName('Convention_Formation'), 'view');
    } catch {
      toast.error('Impossible de charger la Convention');
    }
  };

  const openSignatureDialog = (devis) => {
    setSelectedDevis(devis);
    setShowSignatureDialog(true);
  };

  const [useProfileSigDevis, setUseProfileSigDevis] = useState(false);

  const handleSignerDevis = async () => {
    let signature;
    if (useProfileSigDevis && savedSignature) {
      signature = savedSignature;
    } else {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        toast.error('Veuillez dessiner votre signature');
        return;
      }
      signature = signatureRef.current.toDataURL();
    }
    
    try {
      await axios.post(`/stagiaire/devis/${selectedDevis.devis_id}/signer`, null, {
        params: { signature }
      });
      toast.success('Devis signe avec succes !');
      setShowSignatureDialog(false);
      setUseProfileSigDevis(false);
      loadMesDevis();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard-stagiaire">
      <DocumentsAccueil user={user} />
      <TuileLivretBase user={user} />
      {/* BANNIERE Convention en attente de signature */}
      {convStage && convStage.statut && (convStage.statut === 'en_attente_signatures' || convStage.statut === 'partiellement_signe') && !convStage.signature_stagiaire && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-5 text-white shadow-lg animate-pulse-slow" data-testid="banner-convention-signature">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 rounded-full p-3">
                <PenTool className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Convention de Mise en Situation Pédagogique à signer</h3>
                <p className="text-white/90 text-sm">Votre convention tripartite est prête et attend votre signature.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" onClick={handleDownloadConvStage}>
                <FileText className="h-4 w-4 mr-2" /> Consulter
              </Button>
              <Button size="sm" className="bg-white text-orange-600 hover:bg-white/90 font-bold" onClick={() => setShowSignConvStageDialog(true)}>
                <PenTool className="h-4 w-4 mr-2" /> Signer maintenant
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* BANNIERE Autonomies en attente de validation */}
      {autonomiesEnAttente > 0 && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-5 text-white shadow-lg" data-testid="banner-autonomie-validation">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 rounded-full p-3 animate-bounce">
                <Shield className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">
                  {autonomiesEnAttente} autonomie{autonomiesEnAttente > 1 ? 's' : ''} en attente de votre validation !
                </h3>
                <p className="text-white/90 text-sm">Votre tuteur et l'entreprise ont validé vos compétences. Confirmez ou refusez votre autonomie.</p>
              </div>
            </div>
            <a href="/validation-autonomie-stagiaire">
              <Button size="sm" className="bg-white text-purple-600 hover:bg-white/90 font-bold">
                <CheckCircle className="h-4 w-4 mr-2" /> Valider maintenant
              </Button>
            </a>
          </div>
        </div>
      )}

      {/* Header Bandeau Vert - Copié du Profil */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#6BA82A] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Photo */}
          <div className="flex-shrink-0">
            <div className="w-36 h-44 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden" data-testid="profile-photo">
              <PhotoIdentite
                userId={user?.user_id}
                className="w-full h-full object-cover"
                testId="stagiaire-header-photo"
                fallback={
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="h-14 w-14 text-white/60" />
                  </div>
                }
              />
            </div>
          </div>
          
          {/* Infos principales */}
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-white/80 text-sm">{stagiaire?.genre || ''}</span>
              <h1 className="text-2xl md:text-3xl font-bold">
                {user.prenom} {user.nom}
              </h1>
              {user.numero_candidat && (
                <span className="text-sm font-mono bg-white/20 px-2.5 py-0.5 rounded-full" data-testid="stagiaire-numero-candidat">
                  N°{user.numero_candidat}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
              {stagiaire?.date_naissance && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Né(e) le {new Date(stagiaire.date_naissance).toLocaleDateString('fr-FR')}
                  {stagiaire?.lieu_de_naissance && ` à ${stagiaire.lieu_de_naissance}`}
                </span>
              )}
            </div>
            
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="font-semibold flex items-center gap-2 mb-2">
                <GraduationCap className="h-5 w-5" />
                {stagiaire?.diplome || 'BPJEPS Activités du Motonautisme et Disciplines Associées'}
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <Building2 className="h-4 w-4" />
                  Centre {stagiaire?.centre_formation || 'Atlantique'}
                </span>
                {stagiaire?.entreprise_info && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    Entreprise : {stagiaire.entreprise_info.nom_entreprise || stagiaire.entreprise_info.nom || ''}
                  </span>
                )}
                {stagiaire?.tuteur_info && (
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    Tuteur : {stagiaire.tuteur_info.prenom || ''} {stagiaire.tuteur_info.nom || ''}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                  {stagiaire?.statut === 'apprenti' ? 'Apprenti' : 'Stagiaire'}
                </span>
              </div>
            </div>
            <Button 
              onClick={() => setCarteOpen(true)} 
              className="mt-2 bg-white text-[#0a1628] hover:bg-blue-50 shadow-md" 
              size="sm"
              data-testid="btn-carte-etudiant"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Ma Carte Etudiant
            </Button>
          </div>
          
          {/* Stats absences */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg" data-testid="stat-absences">
              <UserX className="h-5 w-5 text-red-300" />
              <div className="text-center">
                <p className="text-lg font-bold">{presenceStats.absences}</p>
                <p className="text-xs text-white/80">Absences</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg" data-testid="stat-retards">
              <Timer className="h-5 w-5 text-orange-300" />
              <div className="text-center">
                <p className="text-lg font-bold">{presenceStats.retards}</p>
                <p className="text-xs text-white/80">Retards</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg" data-testid="stat-justifiees">
              <FileCheck className="h-5 w-5 text-green-300" />
              <div className="text-center">
                <p className="text-lg font-bold">{presenceStats.justifiees}</p>
                <p className="text-xs text-white/80">Abs. Justifiées</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zen Button - Météo et Marées */}
      <div className="flex justify-end gap-2">
        <a href="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000051901186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300" data-testid="referentiel-diplome-btn">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
          <span className="hidden sm:inline">Référentiel diplôme</span>
          <span className="sm:hidden">Réf.</span>
        </a>
        <ZenButton />
      </div>

      {/* Notifications */}
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-[#8DC63F]" data-testid="stat-moyenne">
          <div className="flex items-center space-x-4">
            <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
              <TrendingUp className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats?.moyenne_generale || 0}/10</p>
              <p className="text-sm text-slate-600">Ma Moyenne</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-purple-500" data-testid="stat-heures">
          <div className="flex items-center space-x-4">
            <div className="bg-purple-500/10 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{heuresTotal}h</p>
              <p className="text-sm text-slate-600">Heures de Stage</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm border-l-4 border-l-orange-500" data-testid="stat-notes">
          <div className="flex items-center space-x-4">
            <div className="bg-orange-500/10 p-3 rounded-lg">
              <Award className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="text-3xl font-display font-bold text-[#0F172A]">{stats?.total_notes || 0}</p>
              <p className="text-sm text-slate-600">Évaluations</p>
            </div>
          </div>
        </div>

        <AutonomieActionCard linkTo="/validation-autonomie-stagiaire" />

        <NotificationWidget />
      </div>

      {/* Barre de progression du dossier */}
      {(() => {
        const etapes = dossierStatus?.dossier_etapes || {};
        const steps = [
          { key: 'infos_administratives', label: 'Informations administratives' },
          { key: 'pieces_fournir', label: 'Pièces à fournir' },
          { key: 'paiement_frais', label: 'Paiement des frais' },
        ];
        const nbValides = steps.filter(s => etapes[s.key]).length;
        const pct = Math.round((nbValides / steps.length) * 100);
        const isComplete = nbValides === steps.length;
        const notifications = dossierStatus?.notifications || [];
        const hasRefused = notifications.some(n => n.type === 'document_refuse');

        return (
          <div className={`p-4 rounded-lg border-l-4 ${isComplete ? 'bg-green-50 border-green-500' : hasRefused ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`} data-testid="dossier-progress">
            <div className="flex items-center gap-3 mb-2">
              {isComplete ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : (
                <AlertCircle className="h-6 w-6 text-amber-500" />
              )}
              <div className="flex-1">
                <h3 className={`font-bold ${isComplete ? 'text-green-800' : 'text-amber-800'}`}>
                  {isComplete ? 'Dossier validé' : 'Dossier incomplet'}
                </h3>
                <p className="text-sm text-slate-600">{nbValides} / {steps.length} étapes validées par le centre</p>
              </div>
              <span className={`text-2xl font-bold ${isComplete ? 'text-green-600' : 'text-amber-600'}`}>{pct}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-amber-400'}`} style={{ width: `${pct}%` }} />
            </div>
            {!isComplete && (
              <div className="mt-3 space-y-1">
                {steps.map(s => (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    {etapes[s.key] ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-slate-400" />
                    )}
                    <span className={etapes[s.key] ? 'text-green-700' : 'text-slate-500'}>{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Résultats tests de sélection */}
      {testsSelection && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="resultats-tests">
          <h3 className="font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
            <Award className="h-5 w-5 text-[#8DC63F]" />
            Résultats des tests de sélection
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {testsSelection.epreuve_vnm_bras && (
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-xs text-slate-500">VNM Bras</p>
                <p className="text-xl font-bold text-[#0F172A]">{testsSelection.epreuve_vnm_bras}</p>
              </div>
            )}
            {testsSelection.epreuve_vnm_selle && (
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-xs text-slate-500">VNM Selle</p>
                <p className="text-xl font-bold text-[#0F172A]">{testsSelection.epreuve_vnm_selle}</p>
              </div>
            )}
            {testsSelection.epreuve_entretien && (
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-xs text-slate-500">Entretien</p>
                <p className="text-xl font-bold text-[#0F172A]">{testsSelection.epreuve_entretien}</p>
              </div>
            )}
            <div className={`p-3 rounded-lg text-center ${['admis','recu'].includes(testsSelection.resultat_global) ? 'bg-green-50' : 'bg-red-50'}`}>
              <p className="text-xs text-slate-500">Résultat</p>
              <p className={`text-xl font-bold ${['admis','recu'].includes(testsSelection.resultat_global) ? 'text-green-600' : 'text-red-600'}`}>
                {['admis','recu'].includes(testsSelection.resultat_global) ? 'Admis' : 'Non admis'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Documents de la candidature - visibles par le stagiaire */}
      {candidatDocs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="mes-documents-candidature">
          <button
            onClick={() => {
              const el = document.getElementById('docs-candidature-list');
              if (el) el.classList.toggle('hidden');
            }}
            className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#8DC63F]" />
              <span className="text-lg font-semibold text-[#0F172A]">
                Mes documents déposés ({candidatDocs.length})
              </span>
            </div>
            <ChevronDown className="h-5 w-5 text-slate-400" />
          </button>
          <div id="docs-candidature-list" className="hidden border-t border-slate-100 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {candidatDocs.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2 min-w-0">
                    {doc.statut === 'accepte' ? (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    ) : doc.statut === 'refuse' ? (
                      <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-500 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{doc.type_document || doc.nom || 'Document'}</p>
                      {doc.nom_fichier && <p className="text-xs text-slate-400 truncate">{doc.nom_fichier}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      doc.statut === 'accepte' ? 'bg-green-100 text-green-700' :
                      doc.statut === 'refuse' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {doc.statut === 'accepte' ? 'Validé' : doc.statut === 'refuse' ? 'Refusé' : 'En attente'}
                    </span>
                    {doc.document_id && (
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`/candidats/doc-view/${doc.document_id}`, { responseType: 'blob' });
                            openPdfBlob(res.data, doc.nom_fichier || doc.type_document || 'document.pdf', 'view');
                          } catch { toast.error('Impossible de charger le document'); }
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        title="Voir"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Documents de Formation (PIF & Convention) */}
      {(pifData || conventionData) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pifData && (
            <Card className="border-l-4 border-purple-500" data-testid="stagiaire-pif-card">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-purple-600" />
                    <h3 className="font-semibold text-slate-800">Plan Individuel de Formation</h3>
                  </div>
                  <Badge className="bg-green-500">Signé</Badge>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Signé le {pifData.date_signature} — {pifData.total_heures_formation || 0}h formation{pifData.total_heures_foad ? ` + ${pifData.total_heures_foad}h FOAD` : ''} + {pifData.total_heures_stage || 0}h stage
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleViewPIF} data-testid="btn-view-pif-stagiaire">
                    <Eye className="h-4 w-4 mr-2" />
                    Consulter
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadPIF} data-testid="btn-download-pif-stagiaire">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {conventionData && (
            <Card className="border-l-4 border-blue-500" data-testid="stagiaire-convention-card">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">Convention de Formation</h3>
                  </div>
                  <Badge className="bg-green-500">Signée</Badge>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Signée le {conventionData.date_signature} — Vous êtes inscrit(e) en formation
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleViewConvention} data-testid="btn-view-convention-stagiaire">
                    <Eye className="h-4 w-4 mr-2" />
                    Consulter
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadConvention} data-testid="btn-download-convention-stagiaire">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Convention de Mise en Situation Pédagogique (tripartite) */}
          {convStage && convStage.statut && (convStage.statut === 'en_attente_signatures' || convStage.statut === 'partiellement_signe') && !convStage.signature_stagiaire && (
            <Card className="border-l-4 border-orange-500" data-testid="stagiaire-conv-stage-sign-card">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <PenTool className="h-5 w-5 text-orange-600" />
                    <h3 className="font-semibold text-slate-800">Convention de Mise en Situation Pédagogique</h3>
                  </div>
                  <Badge className="bg-orange-500">En attente de signature</Badge>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Votre convention de mise en situation pédagogique tripartite est prête. Veuillez la consulter et la signer.
                </p>
                {/* Indicateurs des 4 signatures */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="conv-stage-pending-status">
                  {[
                    { key: 'signature_centre', label: 'Centre FISL' },
                    { key: 'signature_stagiaire', label: 'Stagiaire' },
                    { key: 'signature_tuteur', label: 'Tuteur' },
                    { key: 'signature_entreprise', label: 'Entreprise' },
                  ].map(({ key, label }) => {
                    const signed = !!(convStage[key] || convStage[`${key}_present`]);
                    return (
                      <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${signed ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-500 border border-slate-200'}`} data-testid={`conv-pending-status-${key}`}>
                        {signed ? <CheckCircle className="h-4 w-4 shrink-0" /> : <Clock className="h-4 w-4 shrink-0" />}
                        <span className="truncate">{label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={handleViewConvStage} data-testid="btn-view-conv-stage-stagiaire">
                    <Eye className="h-4 w-4 mr-2" />
                    Consulter
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadConvStage} data-testid="btn-download-conv-stage-stagiaire-pre">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setShowSignConvStageDialog(true)} data-testid="btn-sign-conv-stage-stagiaire">
                    <PenTool className="h-4 w-4 mr-2" />
                    Signer la Convention
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {convStage && convStage.statut && convStage.signature_stagiaire && (
            <Card className="border-l-4 border-green-500" data-testid="stagiaire-conv-stage-signed-card">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <h3 className="font-semibold text-slate-800">Convention de Mise en Situation Pédagogique</h3>
                  </div>
                  <Badge className={convStage.statut === 'signe' ? 'bg-green-500' : 'bg-amber-500'}>
                    {convStage.statut === 'signe' ? 'Signée (complète)' : 'Vous avez signé'}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  {convStage.statut === 'signe'
                    ? 'Toutes les signatures ont été recueillies. Convention archivée.'
                    : 'Vous avez signé. En attente des autres signatures.'}
                </p>
                {/* Indicateurs des 4 signatures */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="conv-stage-signatures-status">
                  {[
                    { key: 'signature_centre', label: 'Centre FISL' },
                    { key: 'signature_stagiaire', label: 'Stagiaire' },
                    { key: 'signature_tuteur', label: 'Tuteur' },
                    { key: 'signature_entreprise', label: 'Entreprise' },
                  ].map(({ key, label }) => {
                    const signed = !!(convStage[key] || convStage[`${key}_present`]);
                    return (
                      <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${signed ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-500 border border-slate-200'}`} data-testid={`conv-status-${key}`}>
                        {signed ? <CheckCircle className="h-4 w-4 shrink-0" /> : <Clock className="h-4 w-4 shrink-0" />}
                        <span className="truncate">{label}</span>
                      </div>
                    );
                  })}
                </div>
                <Button variant="outline" size="sm" onClick={handleViewConvStage} data-testid="btn-view-conv-stage-stagiaire-signed">
                  <Eye className="h-4 w-4 mr-2" />
                  Consulter
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadConvStage} data-testid="btn-download-conv-stage-stagiaire">
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger la Convention
                </Button>
              </CardContent>
            </Card>
          )}
          {convStage && convStage.statut && !convStage.signature_stagiaire && convStage.statut !== 'en_attente_signatures' && convStage.statut !== 'partiellement_signe' && (
            <Card className="border-l-4 border-blue-500" data-testid="stagiaire-conv-stage-view-card">
              <CardContent className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-slate-800">Convention de Mise en Situation Pédagogique</h3>
                  </div>
                  <Badge className={convStage.statut === 'signe' ? 'bg-green-500' : 'bg-slate-500'}>
                    {convStage.statut === 'signe' ? 'Signée' : convStage.statut === 'en_attente_validation_centre' ? 'Validation en cours' : 'Brouillon'}
                  </Badge>
                </div>
                <Button variant="outline" size="sm" onClick={handleViewConvStage} data-testid="btn-view-conv-stage-other">
                  <Eye className="h-4 w-4 mr-2" />
                  Consulter
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownloadConvStage} data-testid="btn-download-conv-stage-other" className="ml-2">
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Grille d'évaluation tuteur (lecture seule) */}
      <GrilleEvaluationReadOnly
        stagiaireId={user.user_id}
        testIdPrefix="stagiaire-grille"
      />

      {/* Compétences Validées */}
      {autonomiesValidees.length > 0 && (
        <CompetencesValidees autonomies={autonomiesValidees} />
      )}

      {/* Prérequis et Blocs de Compétences */}
      <CompetencesPrerequisConsultation stagiaireId={user.user_id} hideEpmsp={true} />

      {/* Quick Actions */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 mb-6">
          <FileText className="h-6 w-6 text-[#8DC63F]" />
          <h2 className="text-2xl font-display font-semibold text-[#0F172A]">Accès Rapide</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="/validation-autonomie-stagiaire" className="relative p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors" data-testid="quick-link-autonomie">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-[#0F172A]">Validation d'Autonomie</p>
                <p className="text-sm text-slate-600">Accepter ou refuser vos compétences</p>
              </div>
              {autonomiesEnAttente > 0 && (
                <span className="flex items-center justify-center w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full animate-pulse">
                  {autonomiesEnAttente}
                </span>
              )}
            </div>
          </a>
          <a href="/progression" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors" data-testid="quick-link-progression">
            <p className="font-semibold text-[#0F172A]">Mes Notes et Progression</p>
            <p className="text-sm text-slate-600">Consulter vos évaluations</p>
          </a>
          <a href="/carnets-notes" className="p-4 border border-slate-200 rounded-lg hover:border-orange-400 transition-colors border-l-4 border-l-orange-400" data-testid="quick-link-carnets">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-orange-500" />
              <p className="font-semibold text-[#0F172A]">Carnets de Notes</p>
            </div>
            <p className="text-sm text-slate-600">Notes Centre + Évaluations Tuteur</p>
          </a>
          <a href="/documents" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors" data-testid="quick-link-documents">
            <p className="font-semibold text-[#0F172A]">Mes Documents</p>
            <p className="text-sm text-slate-600">Attestations, permis, certificats</p>
          </a>
          <a href="/heures-stage" className="p-4 border border-slate-200 rounded-lg hover:border-[#8DC63F] transition-colors" data-testid="quick-link-heures">
            <p className="font-semibold text-[#0F172A]">Heures de Stage</p>
            <p className="text-sm text-slate-600">Suivi de votre stage</p>
          </a>
          <a href="https://fisl.askott.fr/login/index.php" target="_blank" rel="noopener noreferrer" className="p-4 border border-slate-200 rounded-lg hover:border-blue-500 transition-colors border-l-4 border-l-blue-500" data-testid="quick-link-moodle">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-600" />
              <p className="font-semibold text-[#0F172A]">Plateforme E-Learning</p>
            </div>
            <p className="text-sm text-slate-600">Accéder à Moodle (cours en ligne)</p>
          </a>
        </div>
      </div>

      {/* Section Épreuves EPMSP */}
      <EPMSPSectionStagiaire user={user} />

      {/* Demande d'absence justifiée */}
      <DemandeAbsenceJustifiee user={user} onSuccess={loadStagiaireData} />

      {/* Mes Devis et Factures */}
      {(mesDevis.length > 0 || mesFactures.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mes Devis */}
          {mesDevis.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#8DC63F]" />
                  Mes Devis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mesDevis.map((devis, idx) => (
                    <div key={idx} className="p-4 bg-green-50 rounded-lg border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm bg-green-100 px-2 py-1 rounded">#{devis.devis_id}</span>
                        <Badge className={
                          devis.statut === 'accepte' ? 'bg-green-500' :
                          devis.statut === 'envoye' ? 'bg-blue-500' :
                          devis.statut === 'refuse' ? 'bg-red-500' : 'bg-slate-500'
                        }>
                          {devis.statut === 'accepte' ? 'Accepté' :
                           devis.statut === 'envoye' ? 'En attente' :
                           devis.statut === 'refuse' ? 'Refusé' : 'Brouillon'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center mb-3">
                        <div className="text-sm text-slate-600">
                          <p>Créé le: {new Date(devis.date_creation).toLocaleDateString('fr-FR')}</p>
                          {devis.bc_codes?.length > 0 && <p className="text-xs">BC: {devis.bc_codes.join(', ')}</p>}
                        </div>
                        <p className="text-xl font-bold text-green-700">{(devis.montant_total || 0).toLocaleString('fr-FR')} €</p>
                      </div>
                      
                      {devis.statut === 'accepte' && devis.signature && (
                        <div className="mb-3 p-2 bg-green-100 rounded text-sm text-green-700">
                          ✓ Signé le {new Date(devis.date_acceptation).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadDevis(devis.devis_id)}
                          data-testid={`download-devis-${devis.devis_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Télécharger
                        </Button>
                        
                        {devis.statut !== 'accepte' && devis.statut !== 'refuse' && (
                          <Button
                            size="sm"
                            className="bg-[#8DC63F] hover:bg-[#7AB62F]"
                            onClick={() => openSignatureDialog(devis)}
                            data-testid={`signer-devis-${devis.devis_id}`}
                          >
                            <PenTool className="h-4 w-4 mr-1" />
                            Signer et accepter
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Mes Factures */}
          {mesFactures.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  Mes Factures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {mesFactures.map((facture, idx) => (
                    <div key={idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">{facture.facture_numero}</span>
                        <Badge className={
                          facture.statut === 'payee' ? 'bg-green-500' :
                          facture.statut === 'partielle' ? 'bg-orange-500' : 'bg-blue-500'
                        }>
                          {facture.statut === 'payee' ? 'Payée' :
                           facture.statut === 'partielle' ? 'Partiellement payée' : 'En attente'}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-slate-600">
                          <p>{new Date(facture.date_creation).toLocaleDateString('fr-FR')}</p>
                          {(facture.total_paye || 0) > 0 && (
                            <p className="text-xs text-green-600">Payé: {facture.total_paye.toLocaleString('fr-FR')} €</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-blue-700">{(facture.montant_total || 0).toLocaleString('fr-FR')} €</p>
                          {facture.statut !== 'payee' && (
                            <p className="text-xs text-orange-600">Reste: {(facture.solde_restant || facture.montant_total).toLocaleString('fr-FR')} €</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Numéro d'Urgence / Harcèlement / Discriminations */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg p-6 shadow-sm" data-testid="urgence-harcelement-card">
        <div className="flex items-start space-x-4">
          <div className="bg-red-100 p-3 rounded-full">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-display font-bold text-red-700 mb-2">
              Besoin d'aide ? Urgence, Harcèlement ou comportement discriminatoires
            </h2>
            <p className="text-slate-700 mb-4">
              Vous êtes témoin ou victime d'une violence dans le sport :
            </p>

            {/* Alerter par mail */}
            <div className="flex items-center gap-3 mb-4 p-3 bg-red-100/60 rounded-lg border border-red-200">
              <Mail className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-800">Alerter par mail :</p>
                <a href="mailto:signal-sports@sports.gouv.fr" className="text-red-700 underline font-medium" data-testid="signal-sports-email">
                  signal-sports@sports.gouv.fr
                </a>
              </div>
            </div>

            {/* Numéros d'urgence prioritaires */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <a 
                href="tel:17" 
                className="flex items-center justify-center space-x-3 bg-red-700 hover:bg-red-800 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                data-testid="urgence-17-button"
              >
                <Phone className="h-5 w-5" />
                <span>17 - Police</span>
              </a>
              <a 
                href="tel:114" 
                className="flex items-center justify-center space-x-3 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                data-testid="urgence-114-button"
              >
                <Phone className="h-5 w-5" />
                <span>114 - Urgence SMS</span>
              </a>
              <a 
                href="tel:0637285884" 
                className="flex items-center justify-center space-x-3 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                data-testid="urgence-phone-button"
              >
                <Phone className="h-5 w-5" />
                <span>06 37 28 58 84 - Ligne FISL</span>
              </a>
            </div>

            {/* Autres numéros utiles */}
            <div className="pt-3 border-t border-red-200">
              <p className="text-sm text-slate-600 mb-2">
                <strong>Autres numéros utiles :</strong>
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="text-slate-700"><strong>SAMU :</strong> 15</span>
                <span className="text-slate-700"><strong>Pompiers :</strong> 18</span>
                <span className="text-slate-700"><strong>Secours en mer :</strong> 196</span>
                <span className="text-slate-700"><strong>Urgence européenne :</strong> 112</span>
              </div>
            </div>

            {/* Bouton En savoir plus */}
            <div className="mt-4 pt-4 border-t border-red-200">
              <Button
                onClick={() => setShowGuideDialog(true)}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
                data-testid="en-savoir-plus-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                En savoir plus - Guide explicatif et procédure
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Guide Explicatif */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Shield className="h-5 w-5" />
              Guide explicatif et de procédure
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 text-sm text-slate-700">
            {/* Section Violences sexuelles */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="font-bold text-red-800 mb-3 text-base">Les violences sexuelles : c'est quoi ?</h3>
              <p className="leading-relaxed">
                Les violences à caractère sexuel recouvrent toutes les situations dans lesquelles une personne cherche à imposer à autrui un comportement à connotation sexuelle. Ces violences peuvent prendre diverses formes telles que des propos sexuels ou sexistes, des invitations trop insistantes, du chantage, des menaces, des messages ou images pornographiques, dont résulte une violence qui peut être à la fois verbale, physique et psychologique, dégradant l'image qu'a la victime d'elle-même.
              </p>
              <p className="mt-3 font-semibold text-red-700">
                Vous êtes victime ou témoin : vous devez le signaler au plus vite.
              </p>
            </div>

            {/* Section Comportements Discriminatoires */}
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="font-bold text-orange-800 mb-3 text-base">Comportements discriminatoires à signaler</h3>
              <p className="leading-relaxed mb-3">
                Ce mémo d'information vous informe sur les comportements discriminatoires ou inappropriés pouvant/devant faire l'objet d'un signalement, et la procédure à suivre.
              </p>

              <h4 className="font-semibold text-orange-700 mt-4 mb-2">Qu'est-ce qu'un comportement discriminatoire ?</h4>
              <p className="mb-2">Il s'agit d'un traitement défavorable envers une personne en raison de caractéristiques personnelles telles que :</p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-slate-600">
                <li>Le sexe ou l'identité de genre</li>
                <li>L'origine ethnique ou nationale</li>
                <li>L'orientation sexuelle</li>
                <li>La religion ou les convictions</li>
                <li>Le handicap</li>
                <li>L'âge</li>
                <li>L'apparence physique</li>
              </ul>

              <h4 className="font-semibold text-orange-700 mt-4 mb-2">Comportements à signaler :</h4>
              <ul className="list-disc list-inside space-y-1 ml-2 text-slate-600">
                <li>Propos ou blagues sexistes, racistes ou homophobes récurrentes</li>
                <li>Commentaires déplacés ou humiliants sur l'apparence ou l'identité d'un individu</li>
                <li>Refus d'accès à une activité ou une formation en raison du genre ou de l'origine</li>
                <li>Touchers non sollicités ou regards insistants à connotation sexuelle</li>
                <li>Diffusion d'images ou messages offensants (via affichages, réseaux sociaux, emails)</li>
                <li>Isolement volontaire ou harcèlement répété d'un individu</li>
              </ul>
            </div>

            {/* Section Que faire */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-3 text-base">Que faire si je suis témoin ou victime ?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <span>Parlez-en à une personne de confiance au sein de FISL (Direction, Coordonnateur Pédagogique, Formateur) ou sollicitez un rendez-vous auprès de <strong>Carole Triqueneaux</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <span>Déposez un signalement par mail confidentiel sur <a href="mailto:direction@fisl.fr" className="text-blue-700 underline font-semibold">direction@fisl.fr</a> — <strong>Objet : URGENT SIGNALEMENT</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <span>Alertez les autorités : <a href="mailto:signal-sports@sports.gouv.fr" className="text-blue-700 underline font-semibold">signal-sports@sports.gouv.fr</a></span>
                </li>
              </ul>
            </div>

            {/* Section Droits protégés */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-bold text-green-800 mb-2 text-base flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Vos droits sont protégés
              </h3>
              <p className="leading-relaxed">
                Toute personne signalant un comportement inapproprié est protégée contre toute forme de représailles. La confidentialité est garantie et chaque situation est traitée avec sérieux, confidentialité et bienveillance.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowGuideDialog(false)} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
              J'ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de signature */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="max-w-lg" data-testid="signature-devis-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-[#8DC63F]" />
              Signature électronique - Bon pour acceptation
            </DialogTitle>
            <DialogDescription>
              Devis #{selectedDevis?.devis_id} - {(selectedDevis?.montant_total || 0).toLocaleString('fr-FR')} €
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700 mb-2">
                En signant ce devis, <strong>{user?.prenom} {user?.nom}</strong> accepte les conditions de la formation.
              </p>
              <p className="text-xs text-slate-500">
                Date: {new Date().toLocaleDateString('fr-FR')}
              </p>
            </div>
            
            {savedSignature && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <input type="checkbox" id="useProfileSigDevis" checked={useProfileSigDevis} onChange={(e) => setUseProfileSigDevis(e.target.checked)} className="rounded" />
                <label htmlFor="useProfileSigDevis" className="text-sm text-green-800 cursor-pointer">
                  Utiliser ma signature enregistree
                </label>
              </div>
            )}
            {useProfileSigDevis && savedSignature ? (
              <div className="border-2 border-green-300 rounded-lg p-4 bg-white text-center">
                <img src={savedSignature} alt="Ma signature" className="max-h-32 mx-auto" />
                <p className="text-xs text-green-600 mt-2">Signature de votre profil</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium mb-2 block">Dessinez votre signature ci-dessous:</label>
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-40 bg-white',
                      style: { touchAction: 'none' }
                    }}
                    penColor="#333"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Utilisez votre doigt ou votre stylet pour signer
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={clearSignature}>
              Effacer
            </Button>
            <Button variant="outline" onClick={() => setShowSignatureDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSignerDevis}
              className="bg-[#8DC63F] hover:bg-[#7AB62F]"
              data-testid="confirm-signature-devis-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Valider et signer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue de signature Convention de Mise en Situation Pédagogique */}
      <Dialog open={showSignConvStageDialog} onOpenChange={setShowSignConvStageDialog}>
        <DialogContent className="max-w-lg" data-testid="signature-conv-stage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="h-5 w-5 text-orange-500" />
              Signature - Convention de Mise en Situation Pédagogique
            </DialogTitle>
            <DialogDescription>
              Signez votre convention de mise en situation pédagogique tripartite
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {savedSignature && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <input type="checkbox" id="useProfileSigStag" checked={useProfileSig} onChange={(e) => setUseProfileSig(e.target.checked)} className="rounded" />
                <label htmlFor="useProfileSigStag" className="text-sm text-green-800 cursor-pointer">
                  Utiliser ma signature enregistrée
                </label>
              </div>
            )}
            {useProfileSig && savedSignature ? (
              <div className="border-2 border-green-300 rounded-lg p-4 bg-white text-center">
                <img src={savedSignature} alt="Ma signature" className="max-h-32 mx-auto" />
                <p className="text-xs text-green-600 mt-2">Signature de votre profil</p>
              </div>
            ) : (
              <div>
                <label className="text-sm font-medium mb-2 block">Dessinez votre signature ci-dessous:</label>
                <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={convStageSignRef}
                    canvasProps={{
                      className: 'w-full h-40 bg-white',
                      style: { touchAction: 'none' }
                    }}
                    penColor="#333"
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-slate-400">Utilisez votre doigt ou votre stylet</p>
                  <button onClick={() => convStageSignRef.current?.clear()} className="text-xs text-slate-500 hover:text-red-500">Effacer</button>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowSignConvStageDialog(false); setUseProfileSig(false); }}>
              Annuler
            </Button>
            <Button 
              onClick={handleSignConvStage}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-signature-conv-stage-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Valider et signer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Félicitations Diplôme */}
      <Dialog open={showDiplomeDialog} onOpenChange={dismissDiplomeDialog}>
        <DialogContent className="sm:max-w-lg text-center">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg animate-bounce">
              <Award className="h-10 w-10 text-white" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">
                Félicitations !
              </DialogTitle>
              <DialogDescription className="text-base text-[#0F172A] font-medium">
                Vous avez obtenu un avis favorable pour l'obtention de votre diplôme.
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-slate-500 leading-relaxed max-w-sm">
              La validation définitive interviendra après la réunion plénière organisée par la DRAJES, qui confirmera la conformité des épreuves.
            </p>
            <p className="text-base font-semibold text-[#8DC63F] mt-2">
              Encore bravo pour votre réussite !
            </p>
          </div>
          <DialogFooter className="justify-center">
            <Button onClick={dismissDiplomeDialog} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white px-8" data-testid="dismiss-diplome-btn">
              Merci !
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CarteEtudiantModal
        open={carteOpen}
        onClose={setCarteOpen}
        stagiaire={stagiaire}
        centreProfile={centreProfile}
        user={user}
      />
    </div>
  );
};

export default DashboardStagiaire;